/**
 * Registry for post-backed entity kinds and lightweight frontend routing helpers.
 * Aligns with `apiRoutes.posts` and `EntityKind` in `util/posts.ts`.
 */

import type { EntityKind } from "util/posts";

/** Post kinds that use `GET /api/posts/:id` for detail fetches. */
export const POST_BACKED_KINDS: ReadonlySet<EntityKind> = new Set<EntityKind>([
  "impact",
  "teabate",
  "solution",
  "action",
  "feedback",
  "collection",
]);

/**
 * @param entityType - Route or domain entity type string.
 * @returns True when detail fetch should use the unified posts endpoint.
 */
export function isPostBackedKind(entityType: string): entityType is EntityKind {
  return POST_BACKED_KINDS.has(entityType as EntityKind);
}

/**
 * Client-side profile path for a post-backed entity by kind and numeric id.
 *
 * @param kind - Concrete post kind.
 * @param id - Post id.
 */
export function getPostKindProfilePath(kind: EntityKind, id: number | string): string {
  return `/${kind}/${id}`;
}

/**
 * Client-side path for an issue by canonical name (cname).
 *
 * @param cname - Issue cname (underscores).
 */
export function getIssueProfilePath(cname: string): string {
  return `/issue/${cname}`;
}

/**
 * Client-side path for a user profile.
 */
export function getUserProfilePath(username: string | number): string {
  return `/user/${username}`;
}

/**
 * Client-side path for a team (partea) profile.
 */
export function getTeamProfilePath(teamname: string | number): string {
  return `/partea/${teamname}`;
}

/**
 * Short label used in avatar badges (sentence case / product copy).
 */
export function getEntityBadgeLabel(
  entityType: string,
  options?: { parentLabel?: string }
): string {
  if (options?.parentLabel) {
    return options.parentLabel;
  }
  const map: Record<string, string> = {
    issue: "Issue",
    impact: "Impact",
    teabate: "Teabate",
    solution: "Solution",
    action: "Action",
    feedback: "Feedback",
    collection: "Collection",
    user: "User",
    team: "Team",
    comment: "Comment",
    crosspost: "Crosspost",
    wiki: "Issue",
  };
  return map[entityType] ?? entityType.charAt(0).toUpperCase() + entityType.slice(1);
}
